import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { 
  Users, 
  Plus, 
  Search, 
  Loader2, 
  Pencil, 
  Key, 
  UserCheck, 
  UserX,
  Copy,
  Check,
  Shield,
  Calendar,
  Clock,
  Phone,
  Upload,
  Trash2,
  Building2,
  MapPin,
  Eye,
  User,
  Mail,
  Send,
  AlertCircle,
  Stethoscope,
  Unlock
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import api from '../services/api';
import { getApiError } from '@/lib/utils';
import ManageClientAccessModal from '../components/ManageClientAccessModal';

const roleConfig = {
  admin: { label: 'Admin', color: 'bg-purple-100 text-purple-800', icon: Shield },
  senior_scheduler: { label: 'Senior Scheduler', color: 'bg-indigo-100 text-indigo-800', icon: Calendar },
  scheduler: { label: 'Scheduler', color: 'bg-blue-100 text-blue-800', icon: Calendar },
  auditor: { label: 'Auditor', color: 'bg-green-100 text-green-800', icon: UserCheck }
};

// Config for user type badges (Client Users tab)
const userTypeConfig = {
  CLIENT: { label: 'Client', color: 'bg-emerald-100 text-emerald-800', icon: User },
  INTERNAL: { label: 'Internal', color: 'bg-slate-100 text-slate-800', icon: Users }
};

const AVAILABLE_ROLES = ['admin', 'senior_scheduler', 'scheduler', 'auditor'];

// Scheduling roles that are mutually exclusive
const SCHEDULING_ROLES = ['scheduler', 'senior_scheduler'];


// iter418 — Login History Panel
// Inline panel that shows the last N login events for a user.
// Lives inside the User Edit dialog so admins can self-serve the audit
// trail (previously this required hitting the database directly — see
// Michael Booth June 2026 export request).
function UserLoginHistoryPanel({ userId }) {
  const [days, setDays] = React.useState(30);
  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const { toast } = useToast();

  const fetchHistory = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const end = new Date();
      const start = new Date(end.getTime() - days * 24 * 60 * 60 * 1000);
      const toIso = (d) => d.toISOString().split('T')[0];
      const { data } = await api.get(`/users/${userId}/login-history`, {
        params: { start_date: toIso(start), end_date: toIso(end) },
      });
      setEntries(data.entries || []);
    } catch (err) {
      setError(getApiError(err, 'Failed to load login history'));
    } finally {
      setLoading(false);
    }
  }, [userId, days]);

  React.useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const exportCsv = () => {
    const header = 'Login Time (UTC),IP Address,User-Agent\n';
    const lines = entries.map(e => {
      const ua = (e.user_agent || '').replace(/"/g, '""');
      return `${e.logged_in_at},${e.ip_address || ''},"${ua}"`;
    }).join('\n');
    const blob = new Blob([header + lines], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `login-history-user-${userId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'CSV downloaded', description: `${entries.length} login event(s) exported` });
  };

  return (
    <div className="space-y-2" data-testid="login-history-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs">
          <Label className="text-xs">Range:</Label>
          {[7, 30, 90].map(d => (
            <Button
              key={d}
              type="button"
              size="sm"
              variant={days === d ? 'default' : 'outline'}
              className="h-7 px-2 text-xs"
              onClick={() => setDays(d)}
              data-testid={`login-history-range-${d}d`}
            >{d}d</Button>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={exportCsv}
          disabled={entries.length === 0}
          data-testid="login-history-export-csv"
          className="h-7"
        >Export CSV</Button>
      </div>
      {loading && <div className="text-xs text-muted-foreground py-2">Loading…</div>}
      {error && <div className="text-xs text-red-600 py-2">{error}</div>}
      {!loading && !error && entries.length === 0 && (
        <div className="text-xs text-muted-foreground py-2">No logins in this window.</div>
      )}
      {!loading && entries.length > 0 && (
        <div className="max-h-48 overflow-y-auto rounded border bg-background">
          <table className="w-full text-xs">
            <thead className="bg-muted/60 sticky top-0">
              <tr>
                <th className="text-left px-2 py-1 font-medium">When (UTC)</th>
                <th className="text-left px-2 py-1 font-medium">IP Address</th>
                <th className="text-left px-2 py-1 font-medium">User-Agent</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id} className="border-t" data-testid={`login-history-row-${e.id}`}>
                  <td className="px-2 py-1 whitespace-nowrap font-mono">
                    {e.logged_in_at ? e.logged_in_at.replace('T', ' ').slice(0, 19) : '—'}
                  </td>
                  <td className="px-2 py-1 whitespace-nowrap font-mono">{e.ip_address || '—'}</td>
                  <td className="px-2 py-1 text-muted-foreground truncate max-w-[260px]" title={e.user_agent || ''}>
                    {e.user_agent || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


export default function UsersPage() {
  const { isAdmin, isScheduler, user: currentUser } = useAuth();
  const [activeUserIds, setActiveUserIds] = useState(new Set());
  const { region } = useSettings();
  const { toast } = useToast();
  const { startImpersonation: startImpersonationContext, startAuditorView: startAuditorViewContext } = useImpersonation();
  
  // Phone placeholder based on region
  const phonePlaceholder = region === 'UAE' ? '+971 50 123 4567' : '+44 7700 900000';
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState(() => {
    // Default to CLIENT tab for scheduler-only users (they primarily need "View as Client")
    return (!isAdmin && isScheduler) ? 'CLIENT' : 'INTERNAL';
  }); // NEW: User type tab
  const [includeInactive, setIncludeInactive] = useState(false);
  
  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [passwordEmailSent, setPasswordEmailSent] = useState(false);
  const [passwordEmailError, setPasswordEmailError] = useState(null);
  const [passwordResetUser, setPasswordResetUser] = useState(null);

  // Invite dialog state (iter311)
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteUser, setInviteUser] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(null);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Client Access Modal - Phase 2.2B
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [accessModalUser, setAccessModalUser] = useState(null);
  
  // Impersonation - Phase 2.6B
  const [impersonating, setImpersonating] = useState(false);
  const navigate = useNavigate();

  // iter360 — Login diagnostics dialog
  const [diagDialogOpen, setDiagDialogOpen] = useState(false);
  const [diagUser, setDiagUser] = useState(null);
  const [diagData, setDiagData] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const openLoginDiagnostics = async (user) => {
    setDiagUser(user);
    setDiagData(null);
    setDiagDialogOpen(true);
    setDiagLoading(true);
    try {
      const r = await api.get(`/users/${user.id}/login-diagnostics`);
      setDiagData(r.data);
    } catch (err) {
      toast({ title: 'Failed to load diagnostics', description: getApiError(err), variant: 'destructive' });
    } finally {
      setDiagLoading(false);
    }
  };

  const handleUnlockFromDiag = async () => {
    if (!diagUser) return;
    try {
      await api.post(`/users/${diagUser.id}/unlock`);
      toast({ title: 'Account unlocked', description: `${diagUser.email} can now log in again.` });
      // Refresh diagnostics + user list
      const r = await api.get(`/users/${diagUser.id}/login-diagnostics`);
      setDiagData(r.data);
      loadUsers();
    } catch (err) {
      toast({ title: 'Unlock failed', description: getApiError(err), variant: 'destructive' });
    }
  };
  
  // Form data - now with multi-role support, region access, and user_type
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    user_type: 'INTERNAL',
    roles: ['auditor'],
    region_access: 'all',
    phone_number: '',
    profile_photo_url: '',
    can_directly_publish_reports: false  // iter404
  });

  const loadUsers = useCallback(async () => {
    // Guard: if auth context hasn't settled yet (e.g. brief window after auditor-view
    // exit before refreshUser commits), skip and let the effect re-run once isAdmin settles.
    if (!isAdmin && !isScheduler) return;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (includeInactive) params.append('include_inactive', 'true');
      if (roleFilter) params.append('role', roleFilter);
      if (userTypeFilter) params.append('user_type', userTypeFilter);
      
      const response = await api.get(`/users?${params.toString()}`);
      setUsers(response.data);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load users',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast, isAdmin, isScheduler, includeInactive, roleFilter, userTypeFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Fetch active sessions to show online indicators
  useEffect(() => {
    const fetchActiveSessions = async () => {
      try {
        const response = await api.get('/maintenance/sessions');
        const ids = new Set(response.data.sessions?.map(s => s.user_id) || []);
        setActiveUserIds(ids);
      } catch {
        // Non-critical — silently ignore
      }
    };
    fetchActiveSessions();
    const interval = setInterval(fetchActiveSessions, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleRole = (role) => {
    // Only allow role toggling for INTERNAL users
    if (formData.user_type === 'CLIENT') return;
    
    setFormData(prev => {
      const currentRoles = prev.roles || [];
      if (currentRoles.includes(role)) {
        // Don't allow removing all roles for INTERNAL users
        if (currentRoles.length === 1) return prev;
        return { ...prev, roles: currentRoles.filter(r => r !== role) };
      } else {
        // Enforce mutual exclusivity for scheduling roles
        // If adding 'scheduler', remove 'senior_scheduler' and vice versa
        let newRoles = [...currentRoles, role];
        if (role === 'scheduler') {
          newRoles = newRoles.filter(r => r !== 'senior_scheduler');
        } else if (role === 'senior_scheduler') {
          newRoles = newRoles.filter(r => r !== 'scheduler');
        }
        return { ...prev, roles: newRoles };
      }
    });
  };

  const openCreateDialog = () => {
    setSelectedUser(null);
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      user_type: userTypeFilter, // Default to current tab's user type
      roles: userTypeFilter === 'INTERNAL' ? ['auditor'] : [],
      region_access: 'all',
      phone_number: '',
      profile_photo_url: '',
      can_directly_publish_reports: false  // iter404
    });
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    // Normalize roles - handle both array and legacy single role
    let userRoles = user.roles || [];
    if (userRoles.length === 0 && user.role) {
      userRoles = [user.role.toLowerCase()];
    }
    // Normalize to lowercase
    userRoles = userRoles.map(r => r.toLowerCase());
    
    setFormData({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      user_type: user.user_type || 'INTERNAL',
      roles: user.user_type === 'CLIENT' ? [] : (userRoles.length > 0 ? userRoles : ['auditor']),
      region_access: user.region_access || 'all',
      phone_number: user.phone_number || '',
      profile_photo_url: user.profile_photo_url || '',
      can_directly_publish_reports: !!user.can_directly_publish_reports  // iter404
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (selectedUser) {
        // Update existing user — strip roles for CLIENT users
        const payload = { ...formData };
        if (payload.user_type === 'CLIENT') {
          payload.roles = [];
        }
        await api.put(`/users/${selectedUser.id}`, payload);
        toast({ title: 'Success', description: 'User updated' });
        setDialogOpen(false);
      } else {
        // Create new user
        const response = await api.post('/users', formData);
        setTempPassword(response.data.temporary_password);
        setPasswordDialogOpen(true);
        setDialogOpen(false);
      }
      loadUsers();
    } catch (err) {
      console.error('User save error:', err);
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to save user'),
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async (user, sendEmail = false) => {
    try {
      const response = await api.post(`/users/${user.id}/reset-password?send_email=${sendEmail}`);
      setTempPassword(response.data.temporary_password);
      setPasswordEmailSent(response.data.email_sent);
      setPasswordEmailError(response.data.email_error);
      setPasswordResetUser(user);
      setPasswordDialogOpen(true);
      
      if (sendEmail && response.data.email_sent) {
        toast({ title: 'Success', description: 'Password reset and email sent to user' });
      } else if (sendEmail && !response.data.email_sent) {
        toast({ 
          title: 'Partial Success', 
          description: 'Password reset but email failed to send',
          variant: 'warning'
        });
      } else {
        toast({ title: 'Success', description: 'Password reset successfully' });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to reset password'),
        variant: 'destructive'
      });
    }
  };

  // iter311 — send (or regenerate) a user invitation
  const handleSendInvite = async (user, regenerate = false) => {
    setInviteLoading(true);
    setInviteUser(user);
    try {
      const url = regenerate
        ? `/users/${user.id}/invite/regenerate`
        : `/users/${user.id}/invite`;
      const { data } = await api.post(url);
      setInviteResult(data);
      setInviteDialogOpen(true);
      if (data.email_sent) {
        toast({ title: 'Invitation sent', description: `Email delivered to ${data.email}` });
      } else {
        toast({
          title: 'Invitation created',
          description: data.email_error
            ? `Email send failed (${data.email_error}). Copy the link or activation code below and share manually.`
            : 'Copy the invite link or activation code to share manually.',
          variant: 'warning',
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to send invitation'),
        variant: 'destructive',
      });
    } finally {
      setInviteLoading(false);
    }
  };

  const copyToClipboard = async (value, kind) => {
    try {
      await navigator.clipboard.writeText(value);
      setInviteCopied(kind);
      setTimeout(() => setInviteCopied(null), 1800);
    } catch {
      toast({ title: 'Copy failed', description: 'Please select and copy manually.', variant: 'destructive' });
    }
  };


  const handleSendPasswordEmail = async () => {
    if (!passwordResetUser || !tempPassword) return;
    
    try {
      // Re-call reset with email flag - this will generate a new password
      const response = await api.post(`/users/${passwordResetUser.id}/reset-password?send_email=true`);
      setTempPassword(response.data.temporary_password);
      setPasswordEmailSent(response.data.email_sent);
      setPasswordEmailError(response.data.email_error);
      
      if (response.data.email_sent) {
        toast({ title: 'Email Sent', description: `Password sent to ${passwordResetUser.email}` });
      } else {
        toast({ 
          title: 'Email Failed', 
          description: response.data.email_error || 'Could not send email',
          variant: 'destructive'
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to send email'),
        variant: 'destructive'
      });
    }
  };

  const handleActivate = async (user) => {
    try {
      await api.post(`/users/${user.id}/activate`);
      toast({ title: 'Success', description: `${user.first_name} activated` });
      loadUsers();
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to activate user'),
        variant: 'destructive'
      });
    }
  };

  // Handle "View as Auditor" - Admin only, internal auditor users
  const handleViewAsAuditor = async (user) => {
    if (!user.is_active || user.user_type !== 'INTERNAL') return;
    setImpersonating(true);
    try {
      await startAuditorViewContext(user.id);
      toast({
        title: 'Viewing as Auditor',
        description: `Now viewing as ${user.first_name} ${user.last_name}. Session expires in 30 minutes.`,
      });
      // Only navigate to /schedule if still on the users management page.
      // startAuditorViewContext includes a refreshUser() network call, during which
      // the impersonation banner is already visible (checkImpersonation completed).
      // If the user clicks a nav link in that window they will have navigated away;
      // firing navigate('/schedule') afterward would kick them back — that's the bug.
      if (window.location.pathname === '/users') {
        navigate('/schedule');
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to start auditor view'),
        variant: 'destructive',
      });
    } finally {
      setImpersonating(false);
    }
  };

  // Handle "View as Client" impersonation - Phase 2.6B
  const handleViewAsClient = async (user) => {
    if (!user.is_active || user.user_type !== 'CLIENT') return;
    
    setImpersonating(true);
    try {
      // Use context's startImpersonation which updates the global state
      await startImpersonationContext(user.id);
      toast({
        title: 'Viewing as Client',
        description: `Now viewing as ${user.first_name} ${user.last_name}. Session expires in 30 minutes.`
      });
      // Navigate to client portal after context is updated
      navigate('/client/dashboard');
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to start impersonation'),
        variant: 'destructive'
      });
    } finally {
      setImpersonating(false);
    }
  };

  const handleDeactivate = async () => {
    if (!selectedUser) return;
    
    try {
      await api.post(`/users/${selectedUser.id}/deactivate`);
      toast({ title: 'Success', description: `${selectedUser.first_name} deactivated` });
      setDeactivateDialogOpen(false);
      loadUsers();
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to deactivate user'),
        variant: 'destructive'
      });
    }
  };

  const handlePermanentDelete = async () => {
    if (!selectedUser) return;
    
    try {
      await api.delete(`/users/${selectedUser.id}/permanent`);
      toast({ title: 'Success', description: `${selectedUser.first_name} ${selectedUser.last_name} permanently deleted` });
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to delete user'),
        variant: 'destructive'
      });
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(tempPassword);
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
  };

  // Get normalized roles for a user
  const getUserRoles = (user) => {
    let roles = user.roles || [];
    if (roles.length === 0 && user.role) {
      roles = [user.role];
    }
    return roles.map(r => r.toLowerCase());
  };

  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.email.toLowerCase().includes(query) ||
      user.first_name.toLowerCase().includes(query) ||
      user.last_name.toLowerCase().includes(query)
    );
  });

  if (!isAdmin && !isScheduler) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Admin access required</p>
      </div>
    );
  }

  return (
    <div data-testid="users-page">
      <Toaster />

      {/* iter360 — Login Diagnostics Dialog */}
      <Dialog open={diagDialogOpen} onOpenChange={setDiagDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="login-diagnostics-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Stethoscope className="h-5 w-5" /> Login Diagnostics</DialogTitle>
            <DialogDescription>
              Account state for <strong>{diagUser?.first_name} {diagUser?.last_name}</strong> ({diagUser?.email}). Use this when a user reports they can't sign in.
            </DialogDescription>
          </DialogHeader>
          {diagLoading && <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>}
          {!diagLoading && diagData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Status:</span> {diagData.is_active ? <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">Active</Badge> : <Badge variant="secondary" className="bg-red-100 text-red-800">Inactive</Badge>}</div>
                <div><span className="text-muted-foreground">Lock:</span> {diagData.is_locked ? <Badge variant="secondary" className="bg-amber-100 text-amber-800">Locked · {diagData.failed_login_attempts} fails</Badge> : <Badge variant="outline">Not locked</Badge>}</div>
                <div><span className="text-muted-foreground">Must change password:</span> {diagData.must_change_password ? 'Yes' : 'No'}</div>
                <div><span className="text-muted-foreground">User type:</span> {diagData.user_type || '—'}</div>
                <div className="col-span-2"><span className="text-muted-foreground">Last successful login:</span> {diagData.last_login_at ? format(parseISO(diagData.last_login_at), 'dd MMM yyyy HH:mm') : <em>Never</em>}</div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded p-3">
                <p className="text-sm font-medium text-slate-700 mb-1">Diagnosis</p>
                <ul className="text-sm text-slate-700 space-y-1 list-disc list-inside" data-testid="diagnosis-list">
                  {(diagData.diagnosis || []).map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </div>

              {diagData.recent_emails?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-1">Recent emails sent to this address</p>
                  <table className="text-xs w-full border">
                    <thead className="bg-slate-50"><tr><th className="text-left p-1.5">When</th><th className="text-left p-1.5">Type</th><th className="text-left p-1.5">Status</th></tr></thead>
                    <tbody>
                      {diagData.recent_emails.map((em, i) => (
                        <tr key={i} className="border-t"><td className="p-1.5">{em.sent_at ? format(parseISO(em.sent_at), 'dd MMM HH:mm') : '—'}</td><td className="p-1.5">{em.email_type}</td><td className="p-1.5">{em.success ? <span className="text-emerald-700">accepted · {em.status_code}</span> : <span className="text-red-700">failed</span>}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
                <strong>Tip:</strong> if you've emailed a temporary password, ask the user to <em>type</em> it (not paste). Outlook frequently inserts invisible whitespace when copying. AbbeyZone now strips those characters server-side, but typing avoids any ambiguity.
              </div>
            </div>
          )}
          <DialogFooter>
            {diagData?.is_locked && (
              <Button variant="default" onClick={handleUnlockFromDiag} data-testid="diag-unlock-btn">
                <Unlock className="h-4 w-4 mr-2" /> Unlock account
              </Button>
            )}
            {diagUser && (
              <Button variant="outline" onClick={() => { setDiagDialogOpen(false); handleResetPassword(diagUser); }} data-testid="diag-reset-btn">
                <Key className="h-4 w-4 mr-2" /> Reset password
              </Button>
            )}
            <Button variant="ghost" onClick={() => setDiagDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Users className="h-6 w-6" />
            User Management
          </h1>
          <p className="text-muted-foreground">Create and manage user accounts • Assign multiple roles</p>
        </div>
        {isAdmin && (
          <Button onClick={openCreateDialog} data-testid="create-user-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add {userTypeFilter === 'CLIENT' ? 'Client' : 'Internal'} User
          </Button>
        )}
      </div>

      {/* User Type Tabs */}
      <Tabs value={userTypeFilter} onValueChange={setUserTypeFilter} className="mb-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="INTERNAL" className="flex items-center gap-2" data-testid="internal-users-tab">
            <Shield className="h-4 w-4" />
            Internal Users
          </TabsTrigger>
          <TabsTrigger value="CLIENT" className="flex items-center gap-2" data-testid="client-users-tab">
            <Building2 className="h-4 w-4" />
            Client Users
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="user-search-input"
              />
            </div>
            {userTypeFilter === 'INTERNAL' && (
              <Select value={roleFilter || 'all'} onValueChange={(v) => setRoleFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[180px]" data-testid="role-filter">
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="senior_scheduler">Senior Scheduler</SelectItem>
                  <SelectItem value="scheduler">Scheduler</SelectItem>
                  <SelectItem value="auditor">Auditor</SelectItem>
                </SelectContent>
              </Select>
            )}
            <div className="flex items-center space-x-2">
              <Switch
                id="inactive"
                checked={includeInactive}
                onCheckedChange={setIncludeInactive}
                data-testid="include-inactive-toggle"
              />
              <Label htmlFor="inactive" className="text-sm">Show Inactive</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-16">
              <Users className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No users found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>{userTypeFilter === 'CLIENT' ? 'Type' : 'Roles'}</TableHead>
                  <TableHead>Region Access</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead>Last Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map(user => {
                  const userRoles = getUserRoles(user);
                  return (
                    <TableRow 
                      key={user.id} 
                      className={!user.is_active ? 'opacity-50' : ''}
                      data-testid={`user-row-${user.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.profile_photo_url} />
                            <AvatarFallback className="text-xs">
                              {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              {user.first_name} {user.last_name}
                              {activeUserIds.has(user.id) ? (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 border border-green-200" data-testid={`user-online-${user.id}`}>
                                  <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                                  Online{currentUser?.id === user.id ? ' (You)' : ''}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-50 text-slate-400 border border-slate-200" data-testid={`user-offline-${user.id}`}>
                                  <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
                                  Offline
                                </span>
                              )}
                            </div>
                            {user.phone_number && (
                              <div className="text-xs text-muted-foreground flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {user.phone_number}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {/* Show "Client" type for CLIENT users, roles for INTERNAL users */}
                          {user.user_type === 'CLIENT' ? (
                            <Badge className="bg-emerald-100 text-emerald-800 gap-1">
                              <User className="h-3 w-3" />
                              Client
                            </Badge>
                          ) : (
                            userRoles.map(role => {
                              const config = roleConfig[role];
                              if (!config) return null;
                              const Icon = config.icon;
                              return (
                                <Badge key={role} className={`${config.color} gap-1`}>
                                  <Icon className="h-3 w-3" />
                                  {config.label}
                                </Badge>
                              );
                            })
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {/* Region Access Display */}
                        {(() => {
                          const access = user.region_access || 'all';
                          const accessConfig = {
                            all: { label: 'All', color: 'bg-slate-100 text-slate-800' },
                            uk: { label: 'UK Only', color: 'bg-blue-100 text-blue-800' },
                            uae: { label: 'UAE Only', color: 'bg-amber-100 text-amber-800' }
                          };
                          const config = accessConfig[access] || accessConfig.all;
                          return (
                            <Badge className={config.color}>
                              {config.label}
                            </Badge>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {user.is_active ? (
                          <Badge variant="secondary" className="bg-green-100 text-green-800">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-red-100 text-red-800">
                            Inactive
                          </Badge>
                        )}
                        {user.is_locked && (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-800 ml-1">
                            Locked
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.last_login_at ? (
                          <div className="flex items-center text-sm text-muted-foreground" title="Last time the user entered their email and password">
                            <Clock className="h-3 w-3 mr-1" />
                            {format(parseISO(user.last_login_at), 'dd MMM yyyy HH:mm')}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">Never</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.last_seen_at ? (
                          <div
                            className="flex items-center text-sm text-muted-foreground"
                            title="Last successful authentication, including silent session refresh while the user was active in the app"
                            data-testid={`user-last-seen-${user.id}`}
                          >
                            <Clock className="h-3 w-3 mr-1" />
                            {format(parseISO(user.last_seen_at), 'dd MMM yyyy HH:mm')}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm" data-testid={`user-last-seen-${user.id}-never`}>Never</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(user)}
                            title="Edit"
                            data-testid={`edit-user-${user.id}-btn`}
                            className="h-8 w-8"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          )}
                          {/* View as Client button - Phase 2.6B */}
                          {/* Available to Admin and Scheduler roles */}
                          {user.user_type === 'CLIENT' && user.is_active && (isAdmin || isScheduler) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleViewAsClient(user)}
                              title="View as Client"
                              data-testid={`view-as-client-${user.id}-btn`}
                              disabled={impersonating}
                              className="h-8 w-8"
                            >
                              <Eye className="h-4 w-4 text-purple-600" />
                            </Button>
                          )}
                          {/* View as Auditor button — Admin only, internal auditor users */}
                          {user.user_type === 'INTERNAL' && user.is_active && isAdmin &&
                            (user.roles || []).includes('auditor') && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleViewAsAuditor(user)}
                              title="View as Auditor"
                              data-testid={`view-as-auditor-${user.id}-btn`}
                              disabled={impersonating}
                              className="h-8 w-8"
                            >
                              <Eye className="h-4 w-4 text-teal-600" />
                            </Button>
                          )}
                          {/* Manage Access button - only for CLIENT users */}
                          {user.user_type === 'CLIENT' && isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setAccessModalUser(user);
                                setAccessModalOpen(true);
                              }}
                              title="Manage Access"
                              data-testid={`manage-access-${user.id}-btn`}
                              className="h-8 w-8"
                            >
                              <MapPin className="h-4 w-4 text-emerald-600" />
                            </Button>
                          )}
                          {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleSendInvite(user, false)}
                            disabled={inviteLoading}
                            title="Send invitation link"
                            data-testid={`send-invite-${user.id}-btn`}
                            className="h-8 w-8"
                          >
                            {inviteLoading && inviteUser?.id === user.id
                              ? <Loader2 className="h-4 w-4 animate-spin text-violet-500" />
                              : <Send className="h-4 w-4 text-violet-600" />}
                          </Button>
                          )}
                          {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleResetPassword(user)}
                            title="Reset Password"
                            data-testid={`reset-password-${user.id}-btn`}
                            className="h-8 w-8"
                          >
                            <Key className="h-4 w-4 text-amber-600" />
                          </Button>
                          )}
                          {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openLoginDiagnostics(user)}
                            title="Diagnose login problem"
                            data-testid={`diagnose-login-${user.id}-btn`}
                            className="h-8 w-8"
                          >
                            <Stethoscope className={`h-4 w-4 ${user.is_locked ? 'text-red-600' : 'text-slate-500'}`} />
                          </Button>
                          )}
                          {isAdmin && (user.is_active ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setSelectedUser(user);
                                setDeactivateDialogOpen(true);
                              }}
                              title="Deactivate"
                              data-testid={`deactivate-user-${user.id}-btn`}
                              className="h-8 w-8"
                            >
                              <UserX className="h-4 w-4 text-red-500" />
                            </Button>
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleActivate(user)}
                                title="Activate"
                                data-testid={`activate-user-${user.id}-btn`}
                                className="h-8 w-8"
                              >
                                <UserCheck className="h-4 w-4 text-green-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setDeleteDialogOpen(true);
                                }}
                                title="Permanently Delete"
                                data-testid={`delete-user-${user.id}-btn`}
                                className="h-8 w-8"
                              >
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle>
              {selectedUser ? 'Edit User' : `Create ${formData.user_type === 'CLIENT' ? 'Client' : 'Internal'} User`}
            </DialogTitle>
            <DialogDescription>
              {selectedUser 
                ? 'Update user details and roles'
                : formData.user_type === 'CLIENT'
                  ? 'Add a new client portal user'
                  : 'Add a new internal user to the organisation'
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="space-y-4 py-4 px-6 overflow-y-auto flex-1 min-h-0">
              {/* User Type Selection - only for new users */}
              {!selectedUser && (
                <div className="space-y-2">
                  <Label>User Type</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={formData.user_type === 'INTERNAL' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleChange('user_type', 'INTERNAL')}
                      className="flex-1"
                      data-testid="user-type-internal-btn"
                    >
                      <Shield className="h-4 w-4 mr-2" />
                      Internal Staff
                    </Button>
                    <Button
                      type="button"
                      variant={formData.user_type === 'CLIENT' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleChange('user_type', 'CLIENT')}
                      className="flex-1"
                      data-testid="user-type-client-btn"
                    >
                      <Building2 className="h-4 w-4 mr-2" />
                      Client Portal
                    </Button>
                  </div>
                  {formData.user_type === 'CLIENT' && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Client users access the client portal only. They cannot have internal roles.
                    </p>
                  )}
                </div>
              )}
              
              {/* Show user type badge when editing */}
              {selectedUser && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded-md">
                  <Label className="text-muted-foreground text-sm">User Type:</Label>
                  <Badge variant={formData.user_type === 'CLIENT' ? 'secondary' : 'default'}>
                    {formData.user_type === 'CLIENT' ? 'Client Portal' : 'Internal Staff'}
                  </Badge>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="user@company.com"
                  required
                  data-testid="user-email-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    placeholder="John"
                    required
                    data-testid="user-first-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    placeholder="Doe"
                    required
                    data-testid="user-last-name-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone_number">Phone Number</Label>
                <Input
                  id="phone_number"
                  value={formData.phone_number}
                  onChange={(e) => handleChange('phone_number', e.target.value)}
                  placeholder={phonePlaceholder}
                  data-testid="user-phone-input"
                />
              </div>
              
              {/* Multi-Role Selection - only for INTERNAL users */}
              {formData.user_type === 'INTERNAL' && (
                <div className="space-y-2">
                  <Label>Roles</Label>
                  <div className="grid grid-cols-3 gap-2 p-3 border rounded-md bg-muted/30">
                    {AVAILABLE_ROLES.map(role => {
                      const config = roleConfig[role];
                      const Icon = config.icon;
                      const isSelected = formData.roles?.includes(role);
                      return (
                        <label 
                          key={role} 
                          htmlFor={`role-checkbox-${role}`}
                          className={`flex items-center p-2 rounded-md cursor-pointer border transition-all ${
                            isSelected 
                              ? 'bg-primary/10 border-primary' 
                              : 'bg-background border-transparent hover:border-muted-foreground/30'
                          }`}
                          data-testid={`role-toggle-${role}`}
                        >
                          <Checkbox 
                            id={`role-checkbox-${role}`}
                            checked={isSelected}
                            className="mr-2"
                            onCheckedChange={(checked) => {
                              // Prevent unchecking if it's the last role
                              if (!checked && formData.roles?.length === 1) return;
                              toggleRole(role);
                            }}
                          />
                          <Icon className={`h-4 w-4 mr-1 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                          <span className={`text-sm ${isSelected ? 'font-medium' : ''}`}>
                            {config.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Select one or more roles. Users with Auditor role can be assigned to jobs.
                  </p>
                </div>
              )}

              {/* iter404 — Allow Administrators to flag auditors that are
                  trusted to publish reports directly (bypassing approval). */}
              {formData.user_type === 'INTERNAL' && (formData.roles || []).includes('auditor') && isAdmin && (
                <div className="space-y-2">
                  <label
                    htmlFor="can-publish-directly"
                    className="flex items-start gap-3 p-3 rounded-md border bg-muted/30 cursor-pointer"
                    data-testid="can-publish-directly-toggle"
                  >
                    <Checkbox
                      id="can-publish-directly"
                      checked={!!formData.can_directly_publish_reports}
                      onCheckedChange={(checked) =>
                        handleChange('can_directly_publish_reports', !!checked)
                      }
                    />
                    <div className="text-sm leading-tight">
                      <div className="font-medium">Allow direct report publishing</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        When enabled, this auditor can publish Summary Audit Reports and
                        upload attachments directly to clients without going through the
                        scheduler approval workflow. Recommended only for experienced
                        auditors. Admin/Scheduler roles always have this ability.
                      </p>
                    </div>
                  </label>
                </div>
              )}
              
              {/* Region Access - only for INTERNAL users */}
              {formData.user_type === 'INTERNAL' && (
                <div className="space-y-2">
                  <Label htmlFor="region_access">Region Access</Label>
                  <Select 
                    value={formData.region_access || 'all'} 
                    onValueChange={(value) => handleChange('region_access', value)}
                  >
                    <SelectTrigger data-testid="region-access-select">
                      <SelectValue placeholder="Select access scope" />
                    </SelectTrigger>
                    <SelectContent>
                    <SelectItem value="all">All Regions (UK + UAE)</SelectItem>
                    <SelectItem value="uk">UK Only</SelectItem>
                    <SelectItem value="uae">UAE Only</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Restrict which region's data this user can view and manage. Admins always have access to all regions.
                </p>
                </div>
              )}
              
              {/* Client user info */}
              {formData.user_type === 'CLIENT' && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-700">
                    <strong>Client Portal Access</strong>: This user will be able to log in to the client portal 
                    to view published reports and upcoming audits for their assigned sites/groups.
                  </p>
                </div>
              )}
            </div>
            {selectedUser && selectedUser.user_type === 'INTERNAL' && (
              <div className="border-t bg-muted/40 px-6 py-3 shrink-0" data-testid="login-history-section">
                <h4 className="text-sm font-semibold mb-2">Login History (last 30 days)</h4>
                <UserLoginHistoryPanel userId={selectedUser.id} />
              </div>
            )}
            <DialogFooter className="px-6 py-4 border-t shrink-0">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={saving || !formData.email || !formData.first_name || !formData.last_name || (formData.user_type === 'INTERNAL' && formData.roles?.length === 0)}
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {selectedUser ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* iter311 — Invitation Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={(open) => {
        setInviteDialogOpen(open);
        if (!open) {
          setInviteResult(null);
          setInviteUser(null);
          setInviteCopied(null);
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-violet-600" />
              User invitation
            </DialogTitle>
            <DialogDescription>
              {inviteUser && inviteResult && (
                <>Activation details for <strong>{inviteUser.first_name} {inviteUser.last_name}</strong>
                  {' '}({inviteResult.email}).</>
              )}
            </DialogDescription>
          </DialogHeader>
          {inviteResult && (
            <div className="py-2 space-y-4">
              <div className={`flex items-start gap-2 rounded-md p-3 text-sm ${
                inviteResult.email_sent ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'
              }`}>
                {inviteResult.email_sent ? (
                  <><Check className="h-4 w-4 mt-0.5" /> Email sent to {inviteResult.email}.</>
                ) : (
                  <><AlertCircle className="h-4 w-4 mt-0.5" />
                    <span>Email could not be sent{inviteResult.email_error ? `: ${inviteResult.email_error}` : ''}. Copy the link or the 8-character activation code below and share it through another channel — the invitation is still valid.</span>
                  </>
                )}
              </div>

              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Direct invite link (expires {new Date(inviteResult.expires_at).toLocaleString()})</Label>
                <div className="flex items-center gap-2">
                  <Input readOnly value={inviteResult.invite_link} className="font-mono text-xs" data-testid="invite-link-input" />
                  <Button variant="outline" size="sm"
                    onClick={() => copyToClipboard(inviteResult.invite_link, 'link')}
                    data-testid="copy-invite-link-btn">
                    {inviteCopied === 'link' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Activation code (fallback — enter at {inviteResult.activation_page_url})</Label>
                <div className="flex items-center gap-2">
                  <Input readOnly value={inviteResult.activation_code}
                    className="font-mono text-lg tracking-widest" data-testid="invite-code-input" />
                  <Button variant="outline" size="sm"
                    onClick={() => copyToClipboard(inviteResult.activation_code, 'code')}
                    data-testid="copy-invite-code-btn">
                    {inviteCopied === 'code' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Tip: if the user's IT blocks our email or the link, send them the activation code
                via SMS/WhatsApp and point them to the Activate page.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            {inviteUser && (
              <Button variant="outline"
                onClick={() => handleSendInvite(inviteUser, true)}
                disabled={inviteLoading}
                data-testid="regenerate-invite-btn">
                {inviteLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Regenerate &amp; resend
              </Button>
            )}
            <Button onClick={() => setInviteDialogOpen(false)} data-testid="close-invite-dialog-btn">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temporary Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={(open) => {
        setPasswordDialogOpen(open);
        if (!open) {
          setPasswordEmailSent(false);
          setPasswordEmailError(null);
          setPasswordResetUser(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-amber-600" />
              Temporary Password
            </DialogTitle>
            <DialogDescription>
              {passwordResetUser && (
                <>Password reset for <strong>{passwordResetUser.first_name} {passwordResetUser.last_name}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">New Temporary Password</Label>
              <div className="flex items-center gap-2">
                <Input
                  value={tempPassword}
                  readOnly
                  className="font-mono text-lg tracking-wider"
                  data-testid="temp-password-display"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyPassword}
                  title="Copy password"
                  data-testid="copy-password-btn"
                >
                  {passwordCopied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            
            {/* Email Status */}
            {passwordEmailSent && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                <Mail className="h-4 w-4 text-green-600" />
                <span className="text-sm text-green-700">
                  Password email sent to {passwordResetUser?.email}
                </span>
              </div>
            )}
            
            {passwordEmailError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <span className="text-sm text-red-700">
                  Failed to send email: {passwordEmailError}
                </span>
              </div>
            )}
            
            {!passwordEmailSent && !passwordEmailError && (
              <Button
                variant="outline"
                className="w-full"
                onClick={handleSendPasswordEmail}
                data-testid="send-password-email-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                Send Password to User's Email
              </Button>
            )}
            
            <p className="text-sm text-amber-600 flex items-center">
              <Shield className="h-4 w-4 mr-1" />
              User will be required to change this password on first login
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setPasswordDialogOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <AlertDialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate User?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedUser && (
                <>
                  Are you sure you want to deactivate <strong>{selectedUser.first_name} {selectedUser.last_name}</strong>?
                  <br /><br />
                  They will no longer be able to log in until reactivated.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeactivate}
              className="bg-red-600 hover:bg-red-700"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Permanently Delete User?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedUser && (
                <>
                  <strong className="text-red-600">WARNING: This action cannot be undone!</strong>
                  <br /><br />
                  Are you sure you want to permanently delete <strong>{selectedUser.first_name} {selectedUser.last_name}</strong> ({selectedUser.email})?
                  <br /><br />
                  All data associated with this user will be removed from the system.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handlePermanentDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manage Client Access Modal - Phase 2.2B */}
      {accessModalUser && (
        <ManageClientAccessModal
          isOpen={accessModalOpen}
          onClose={() => {
            setAccessModalOpen(false);
            setAccessModalUser(null);
          }}
          userId={accessModalUser.id}
          userName={`${accessModalUser.first_name} ${accessModalUser.last_name}`}
          userEmail={accessModalUser.email}
          onAccessUpdated={() => loadUsers()}
        />
      )}
    </div>
  );
}
